import React from 'react';
import { motion } from 'motion/react';
import { TimeOfDay, WeatherType } from '../types';

interface GardenCloudsProps {
  timeOfDay: TimeOfDay;
  weather: WeatherType;
  minuteOfDay?: number;
  onCloudClick?: (e: React.MouseEvent) => void;
}

interface CloudConfig {
  id: string;
  width: number; // width in px (will scale with responsive styles)
  height: number;
  topPercent: number; // top position in %
  driftDuration: number; // animation duration in seconds
  driftDelay: number; // negative delay in seconds to distribute across screen initially
  opacity: number;
  scale: number;
  zIndex: number;
  beforeSize: { w: number; h: number; top: number; left: number };
  afterSize: { w: number; h: number; top: number; left: number };
  extraPuff?: { w: number; h: number; top: number; left: number };
}

// 12 Distinct cloud formations arranged at varying altitudes and speeds across the sky
const CLOUD_DEFINITIONS: CloudConfig[] = [
  // High altitude wispy background clouds
  {
    id: 'cloud-bg-1',
    width: 140,
    height: 36,
    topPercent: 6,
    driftDuration: 145,
    driftDelay: -18,
    opacity: 0.72,
    scale: 0.85,
    zIndex: 4,
    beforeSize: { w: 64, h: 64, top: -30, left: 24 },
    afterSize: { w: 48, h: 48, top: -20, left: 74 },
  },
  {
    id: 'cloud-bg-2',
    width: 110,
    height: 28,
    topPercent: 12,
    driftDuration: 165,
    driftDelay: -85,
    opacity: 0.65,
    scale: 0.75,
    zIndex: 4,
    beforeSize: { w: 48, h: 48, top: -22, left: 18 },
    afterSize: { w: 36, h: 36, top: -14, left: 56 },
  },
  {
    id: 'cloud-bg-3',
    width: 160,
    height: 38,
    topPercent: 15,
    driftDuration: 130,
    driftDelay: -45,
    opacity: 0.68,
    scale: 0.9,
    zIndex: 5,
    beforeSize: { w: 70, h: 70, top: -34, left: 28 },
    afterSize: { w: 52, h: 52, top: -22, left: 86 },
  },

  // Mid-altitude fluffy cumulus clouds (Core layer from para-ti-mvd.com)
  {
    id: 'cloud-c1',
    width: 125,
    height: 32,
    topPercent: 18,
    driftDuration: 85,
    driftDelay: -10,
    opacity: 0.92,
    scale: 1.05,
    zIndex: 7,
    beforeSize: { w: 56, h: 56, top: -26, left: 18 },
    afterSize: { w: 42, h: 42, top: -18, left: 64 },
    extraPuff: { w: 32, h: 32, top: -12, left: 92 },
  },
  {
    id: 'cloud-c2',
    width: 95,
    height: 26,
    topPercent: 26,
    driftDuration: 110,
    driftDelay: -42,
    opacity: 0.88,
    scale: 0.95,
    zIndex: 6,
    beforeSize: { w: 44, h: 44, top: -20, left: 14 },
    afterSize: { w: 34, h: 34, top: -14, left: 50 },
  },
  {
    id: 'cloud-c3',
    width: 150,
    height: 38,
    topPercent: 10,
    driftDuration: 125,
    driftDelay: -75,
    opacity: 0.85,
    scale: 1.15,
    zIndex: 7,
    beforeSize: { w: 68, h: 68, top: -32, left: 24 },
    afterSize: { w: 50, h: 50, top: -20, left: 80 },
    extraPuff: { w: 38, h: 38, top: -14, left: 112 },
  },
  {
    id: 'cloud-c4',
    width: 80,
    height: 22,
    topPercent: 32,
    driftDuration: 98,
    driftDelay: -20,
    opacity: 0.8,
    scale: 0.85,
    zIndex: 6,
    beforeSize: { w: 38, h: 38, top: -16, left: 12 },
    afterSize: { w: 28, h: 28, top: -12, left: 42 },
  },

  // Additional rich cloud layers (More clouds as requested!)
  {
    id: 'cloud-extra-1',
    width: 135,
    height: 34,
    topPercent: 22,
    driftDuration: 92,
    driftDelay: -60,
    opacity: 0.86,
    scale: 1.0,
    zIndex: 8,
    beforeSize: { w: 58, h: 58, top: -28, left: 22 },
    afterSize: { w: 46, h: 46, top: -18, left: 70 },
  },
  {
    id: 'cloud-extra-2',
    width: 175,
    height: 42,
    topPercent: 8,
    driftDuration: 140,
    driftDelay: -110,
    opacity: 0.9,
    scale: 1.2,
    zIndex: 7,
    beforeSize: { w: 76, h: 76, top: -36, left: 30 },
    afterSize: { w: 56, h: 56, top: -24, left: 94 },
    extraPuff: { w: 42, h: 42, top: -16, left: 130 },
  },
  {
    id: 'cloud-extra-3',
    width: 105,
    height: 28,
    topPercent: 38,
    driftDuration: 105,
    driftDelay: -50,
    opacity: 0.76,
    scale: 0.9,
    zIndex: 6,
    beforeSize: { w: 48, h: 48, top: -22, left: 16 },
    afterSize: { w: 36, h: 36, top: -14, left: 56 },
  },
  {
    id: 'cloud-extra-4',
    width: 120,
    height: 30,
    topPercent: 29,
    driftDuration: 88,
    driftDelay: -30,
    opacity: 0.82,
    scale: 0.95,
    zIndex: 7,
    beforeSize: { w: 52, h: 52, top: -24, left: 18 },
    afterSize: { w: 40, h: 40, top: -16, left: 62 },
  },
  {
    id: 'cloud-extra-5',
    width: 165,
    height: 40,
    topPercent: 35,
    driftDuration: 118,
    driftDelay: -90,
    opacity: 0.78,
    scale: 1.1,
    zIndex: 6,
    beforeSize: { w: 72, h: 72, top: -34, left: 26 },
    afterSize: { w: 54, h: 54, top: -22, left: 88 },
  },
];

export const GardenClouds: React.FC<GardenCloudsProps> = React.memo(({
  timeOfDay,
  weather,
  onCloudClick,
}) => {
  const isCloudyOrRainy = weather === 'cloudy' || weather === 'rainy';

  // Cloud styling & tinting based on sky atmosphere
  const currentCloudStyle = React.useMemo(() => {
    switch (timeOfDay) {
      case 'dawn':
        return {
          background: 'linear-gradient(180deg, #fff5f5 0%, #fee2e2 60%, #fed7aa 100%)',
          boxShadow: '0 8px 24px rgba(244, 114, 182, 0.22)',
          filter: 'drop-shadow(0 4px 12px rgba(251, 146, 60, 0.18))',
          opacityMultiplier: 0.92,
        };
      case 'sunset':
        return {
          background: 'linear-gradient(180deg, #fff7ed 0%, #ffedd5 50%, #fed7aa 100%)',
          boxShadow: '0 8px 26px rgba(234, 88, 12, 0.24)',
          filter: 'drop-shadow(0 4px 14px rgba(249, 115, 22, 0.22))',
          opacityMultiplier: 0.95,
        };
      case 'night':
        return {
          background: 'linear-gradient(180deg, rgba(226, 232, 240, 0.38) 0%, rgba(148, 163, 184, 0.28) 100%)',
          boxShadow: '0 8px 24px rgba(15, 23, 42, 0.35)',
          filter: 'brightness(0.85) drop-shadow(0 4px 16px rgba(56, 189, 248, 0.12))',
          opacityMultiplier: 0.45,
        };
      case 'day':
      default:
        if (weather === 'rainy') {
          return {
            background: 'linear-gradient(180deg, #94a3b8 0%, #64748b 65%, #475569 100%)',
            boxShadow: '0 8px 28px rgba(30, 41, 59, 0.32)',
            filter: 'brightness(0.9) drop-shadow(0 6px 16px rgba(15, 23, 42, 0.25))',
            opacityMultiplier: 0.95,
          };
        }
        if (weather === 'cloudy') {
          return {
            background: 'linear-gradient(180deg, #f8fafc 0%, #e2e8f0 70%, #cbd5e1 100%)',
            boxShadow: '0 8px 24px rgba(100, 116, 139, 0.22)',
            filter: 'brightness(0.98)',
            opacityMultiplier: 0.96,
          };
        }
        return {
          background: 'rgba(255, 255, 255, 0.94)',
          boxShadow: '0 8px 24px rgba(90, 130, 170, 0.16)',
          filter: 'drop-shadow(0 2px 8px rgba(186, 230, 253, 0.25))',
          opacityMultiplier: 1.0,
        };
    }
  }, [timeOfDay, weather]);

  // Cloud click interaction
  const handleCloudClickDefault = (e: React.MouseEvent) => {
    e.stopPropagation();

    if (onCloudClick) {
      onCloudClick(e);
      return;
    }
  };

  return (
    <div
      className="mvd-clouds-container"
      id="para-ti-clouds-layer"
      style={{
        opacity:
          timeOfDay === 'night' && weather === 'sunny'
            ? 0.38
            : isCloudyOrRainy
            ? 0.96
            : 0.88,
      }}
    >
      {/* Dense storm overcast backcloth for rainy / cloudy weather */}
      {isCloudyOrRainy && (
        <div
          className="absolute inset-x-0 top-0 h-64 pointer-events-none transition-opacity duration-1000"
          style={{
            background:
              weather === 'rainy'
                ? 'linear-gradient(180deg, rgba(30, 41, 59, 0.6) 0%, rgba(51, 65, 85, 0.3) 65%, transparent 100%)'
                : 'linear-gradient(180deg, rgba(148, 163, 184, 0.45) 0%, rgba(203, 213, 225, 0.2) 65%, transparent 100%)',
          }}
        />
      )}

      {/* Render all 12 multi-altitude drifting clouds */}
      {CLOUD_DEFINITIONS.map((c) => {
        const effectiveOpacity = c.opacity * currentCloudStyle.opacityMultiplier;

        return (
          <div
            key={c.id}
            onClick={handleCloudClickDefault}
            className="mvd-cloud group"
            style={{
              width: `${c.width}px`,
              height: `${c.height}px`,
              top: `${c.topPercent}%`,
              left: `-${c.width + 60}px`,
              animationDuration: `${c.driftDuration}s`,
              animationDelay: `${c.driftDelay}s`,
              opacity: effectiveOpacity,
              transform: `scale(${c.scale})`,
              zIndex: c.zIndex,
              background: currentCloudStyle.background,
              boxShadow: currentCloudStyle.boxShadow,
              filter: currentCloudStyle.filter,
            }}
            title="Nube mágica — ¡Toca para hacer llover destellos!"
          >
            {/* Lobe 1 (Left puff) */}
            <div
              className="absolute rounded-full pointer-events-none transition-colors duration-500"
              style={{
                width: `${c.beforeSize.w}px`,
                height: `${c.beforeSize.h}px`,
                top: `${c.beforeSize.top}px`,
                left: `${c.beforeSize.left}px`,
                background: currentCloudStyle.background,
              }}
            />

            {/* Lobe 2 (Right puff) */}
            <div
              className="absolute rounded-full pointer-events-none transition-colors duration-500"
              style={{
                width: `${c.afterSize.w}px`,
                height: `${c.afterSize.h}px`,
                top: `${c.afterSize.top}px`,
                left: `${c.afterSize.left}px`,
                background: currentCloudStyle.background,
              }}
            />

            {/* Optional Lobe 3 (Center high puff) */}
            {c.extraPuff && (
              <div
                className="absolute rounded-full pointer-events-none transition-colors duration-500"
                style={{
                  width: `${c.extraPuff.w}px`,
                  height: `${c.extraPuff.h}px`,
                  top: `${c.extraPuff.top}px`,
                  left: `${c.extraPuff.left}px`,
                  background: currentCloudStyle.background,
                }}
              />
            )}

            {/* Soft inner ambient highlight */}
            <div className="absolute inset-x-2 top-1 h-2 rounded-full bg-white/40 blur-[0.5px] pointer-events-none" />
          </div>
        );
      })}
    </div>
  );
});

GardenClouds.displayName = 'GardenClouds';
